<template>
  <div
    class="oblique-grid"
    :class="{ 'oblique-grid--maximized': maximizedPanel }"
    @dblclick.stop
  >
    <div
      v-show="!maximizedPanel || maximizedPanel === 'coronal'"
      class="oblique-pane"
      @dblclick.stop="togglePanel('coronal')"
    >
      <ObliqueSliceViewer
        :view-id="`${viewId}-coronal`"
        outline-type="ObliqueCoronal"
        view-direction="Posterior"
        view-up="Superior"
      />
    </div>
    <div
      v-show="!maximizedPanel || maximizedPanel === 'volume'"
      class="oblique-pane"
      @dblclick.stop="togglePanel('volume')"
    >
      <MultiObliqueSliceViewer
        :view-id="`${viewId}-multi-oblique`"
        view-direction="Posterior"
        view-up="Superior"
        :slices="[
          {
            viewID: `${viewId}-coronal`,
            axis: 'Coronal',
          },
          {
            viewID: `${viewId}-sagittal`,
            axis: 'Sagittal',
          },
          {
            viewID: `${viewId}-axial`,
            axis: 'Axial',
          },
        ]"
      />
    </div>
    <div
      v-show="!maximizedPanel || maximizedPanel === 'sagittal'"
      class="oblique-pane"
      @dblclick.stop="togglePanel('sagittal')"
    >
      <ObliqueSliceViewer
        :view-id="`${viewId}-sagittal`"
        outline-type="ObliqueSagittal"
        view-direction="Right"
        view-up="Superior"
      />
    </div>
    <div
      v-show="!maximizedPanel || maximizedPanel === 'axial'"
      class="oblique-pane"
      @dblclick.stop="togglePanel('axial')"
    >
      <ObliqueSliceViewer
        :view-id="`${viewId}-axial`"
        outline-type="ObliqueAxial"
        view-direction="Superior"
        view-up="Anterior"
      />
    </div>
    <view-overlay-grid class="overlay-no-events view-annotations">
      <template #top-right>
        <v-btn
          v-if="maximizedPanel"
          class="ma-2 pointer-events-all"
          size="small"
          variant="flat"
          aria-label="Restaurar vistas de Oblique"
          @click.stop="maximizedPanel = null"
          @dblclick.stop
        >
          Restaurar vistas
        </v-btn>
      </template>
      <template #bottom-right>
        <div class="annotation-cell" @click.stop>
          <ViewTypeSwitcher :view-id="viewId" :image-id="currentImageID" />
        </div>
      </template>
    </view-overlay-grid>
  </div>
</template>

<script setup lang="ts">
import MultiObliqueSliceViewer from '@/src/components/MultiObliqueSliceViewer.vue';
import ObliqueSliceViewer from '@/src/components/ObliqueSliceViewer.vue';
import useResliceCursorStore from '@/src/store/reslice-cursor';
import ViewOverlayGrid from '@/src/components/ViewOverlayGrid.vue';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import ViewTypeSwitcher from '@/src/components/ViewTypeSwitcher.vue';
import { ref } from 'vue';

interface Props {
  viewId: string;
}

defineProps<Props>();

// Expand the inner pane without remounting the shared reslice views.
type ObliquePanel = 'coronal' | 'volume' | 'sagittal' | 'axial';
const maximizedPanel = ref<ObliquePanel | null>(null);
function togglePanel(panel: ObliquePanel) {
  maximizedPanel.value = maximizedPanel.value === panel ? null : panel;
}

const { currentImageID } = useCurrentImage();

// initialize the reslice cursor store
useResliceCursorStore();
</script>

<style scoped src="@/src/components/styles/vtk-view.css"></style>
<style scoped src="@/src/components/styles/utils.css"></style>
<style scoped>
.oblique-grid {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 1px;
  background-color: gray;
  flex: 1;
}

.oblique-grid--maximized {
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
}

.oblique-grid > div {
  display: flex;
  min-width: 0;
  min-height: 0;
}
</style>
